/**
 * How a dispatch's state is worded (batch 8 T12c1, #3033). The wording follows what the server reports and what that does and does
 * not prove: queued is not sent; sending and uncertain may already have left; sent is acceptance, not delivery; nothing is resent
 * automatically; a cancellation never reaches the supplier.
 */

import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DISPATCH_STATUSES, type DispatchStatus, type DispatchSummary } from './dispatchTypes';
import { formatDateTimeZone } from './dispatchFormat';

const CHIP_COLOR: Record<DispatchStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  queued: 'info',
  sending: 'warning',
  sent: 'success',
  failed: 'error',
  uncertain: 'warning',
  cancelled: 'default',
};

const isKnownStatus = (value: string): value is DispatchStatus =>
  (DISPATCH_STATUSES as readonly string[]).includes(value);

export function DispatchStatusChip({ status }: { status: string }) {
  const { t } = useTranslation('procurementDispatch');
  const known = isKnownStatus(status);
  return (
    <Chip
      size="small"
      color={known ? CHIP_COLOR[status] : 'default'}
      label={known ? t(`status.${status}.label`) : t('status.unknown.label')}
    />
  );
}

interface StatusNoteProps {
  summary: DispatchSummary;
  /** To say "you" instead of naming nobody. */
  currentUserId: string | null;
}

/** The full meaning of the state, the warnings that belong to it, and who/when. */
export function DispatchStatusNote({ summary, currentUserId }: StatusNoteProps) {
  const { t, i18n } = useTranslation('procurementDispatch');
  const language = i18n.language;
  const known = isKnownStatus(summary.status);
  const cancelAsked = summary.cancelRequestedAt !== null && summary.status !== 'cancelled';
  const by = (id: string | null): string =>
    id === null ? t('actor.unknown') : id === currentUserId ? t('actor.you') : t('actor.other');

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">
        {known ? t(`status.${summary.status}.description`) : t('status.unknown.description')}
      </Typography>

      {summary.channel === 'manual' && <Alert severity="info">{t('status.manualChannel')}</Alert>}

      {cancelAsked && summary.requestMayHaveLeft && (
        <Alert severity="warning" role="alert">
          {t('cancel.notPerformed')}
        </Alert>
      )}

      {summary.status === 'failed' && summary.reconciliation === 'not_sent' && (
        <Alert severity="info">{t('status.reconciledNotSent')}</Alert>
      )}
      {summary.status === 'sent' && summary.reconciliation === 'sent' && (
        <Alert severity="info">{t('status.reconciledSent')}</Alert>
      )}

      <Box
        component="dl"
        sx={{
          m: 0,
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          columnGap: 2,
          rowGap: 0.5,
        }}
      >
        <Typography component="dt" variant="body2" color="text.secondary">
          {t('facts.requestedAt')}
        </Typography>
        <Typography component="dd" variant="body2" sx={{ m: 0 }}>
          {formatDateTimeZone(summary.requestedAt, language)} · {by(summary.requestedBy)}
        </Typography>
        <Typography component="dt" variant="body2" color="text.secondary">
          {t('facts.channel')}
        </Typography>
        <Typography component="dd" variant="body2" sx={{ m: 0 }}>
          {t(`channel.${summary.channel}`)}
        </Typography>
        <Typography component="dt" variant="body2" color="text.secondary">
          {t('facts.documentVersion')}
        </Typography>
        <Typography component="dd" variant="body2" sx={{ m: 0 }}>
          {summary.documentVersion}
        </Typography>
        <Typography component="dt" variant="body2" color="text.secondary">
          {t('facts.attempts')}
        </Typography>
        <Typography component="dd" variant="body2" sx={{ m: 0 }}>
          {summary.attemptCount}
        </Typography>
        {summary.sentAt !== null && (
          <>
            <Typography component="dt" variant="body2" color="text.secondary">
              {summary.reconciliation === 'sent' ? t('facts.recordedAt') : t('facts.sentAt')}
            </Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0 }}>
              {formatDateTimeZone(summary.sentAt, language)}
            </Typography>
          </>
        )}
        {cancelAsked && (
          <>
            <Typography component="dt" variant="body2" color="text.secondary">
              {t('facts.cancelAskedAt')}
            </Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0 }}>
              {formatDateTimeZone(summary.cancelRequestedAt, language)} ·{' '}
              {by(summary.cancelRequestedBy)}
            </Typography>
          </>
        )}
        {summary.reconciledAt !== null && (
          <>
            <Typography component="dt" variant="body2" color="text.secondary">
              {t('facts.reconciledAt')}
            </Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0 }}>
              {formatDateTimeZone(summary.reconciledAt, language)} · {by(summary.reconciledBy)}
            </Typography>
          </>
        )}
        {summary.lastErrorCode !== null && (
          <>
            <Typography component="dt" variant="body2" color="text.secondary">
              {t('facts.referenceCode')}
            </Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0, fontFamily: 'monospace' }}>
              {summary.lastErrorCode}
            </Typography>
          </>
        )}
      </Box>
    </Stack>
  );
}
